import { action, computed, observable } from "mobx";

import { ApiUrls, PrincipalConfig } from "@app/AppConstants";
import { PromiseCompletion } from "@app/Classes/PromiseCompletion";
import { AircraftDto } from "@app/Models/WebApiModels";
import ApiService from "@app/Services/ApiService";
import { MatchComparer, stringSearchService } from "@app/Services/StringSearchService";
import { configurationStore } from "@app/Stores/ConfigurationStore";
import { LocalStorageGet, LocalStorageSet } from "@app/Helpers/LocalStorageHelper";

const SELECTED_AIRCRAFT_STORAGE_KEY = 'SelectedAircraftRegistrationNumber';
import { AircraftHelper } from "@app/Helpers/AircraftHelper";

export class AircraftsStore {
    @observable aircrafts: AircraftDto[] = [];
    @observable selectedAircraft: AircraftDto | null = null;
    @observable searchString: string = '';
    @observable includeInactive: boolean = false;
    @observable onlyWk: boolean = true;

    private static readonly ActiveCarrierCode = 'WK';

    public loader: PromiseCompletion = new PromiseCompletion();

    constructor() {
        void this.loadData();
    }

    public async loadData() {
        await this.initFilter();
        await this.loadAircrafts();
    }

    public async initFilter() {
        const configValue: string | null = await configurationStore.get(PrincipalConfig.AircraftsListGroup, PrincipalConfig.ReportFiltersKey, this.loader);
        const filter = configValue ? ApiService.typeCheck(JSON.parse(configValue)) : null;
        this.includeInactive = filter?.includeInactive || false;
        this.onlyWk = filter?.onlyWk ?? true;
        this.searchString = filter?.searchString || '';
    }

    @action
    public async loadAircrafts() {
        const { data } = await ApiService.get<AircraftDto[]>(ApiUrls.AircraftListUrl, null, { completion: this.loader });
        // Server returns aircraft already ordered by type-group SortOrder then registration; keep that order.
        this.aircrafts = data;
    }

    @action.bound
    public onIncludeInactiveChanged(includeInactive: boolean) {
        this.includeInactive = includeInactive;
        this._persistFilter();
    }

    @action.bound
    public onOnlyWkChanged(onlyWk: boolean) {
        this.onlyWk = onlyWk;
        this._persistFilter();
    }

    private _persistFilter() {
        configurationStore.set(
            PrincipalConfig.AircraftsListGroup,
            PrincipalConfig.ReportFiltersKey,
            JSON.stringify({ includeInactive: this.includeInactive, onlyWk: this.onlyWk, searchString: this.searchString })
        );
    }

    @action
    selectAircraft(registrationNumber: string | null) {
        this.selectedAircraft = this.aircrafts.find(a => a.registrationNumber === registrationNumber) || null;
        if (registrationNumber) {
            LocalStorageSet(SELECTED_AIRCRAFT_STORAGE_KEY, registrationNumber);
        }
    }

    @action
    restoreLastSelectedAircraft() {
        if (this.selectedAircraft) return;

        const savedRegistrationNumber = LocalStorageGet<string>(SELECTED_AIRCRAFT_STORAGE_KEY);
        if (savedRegistrationNumber) {
            this.selectAircraft(savedRegistrationNumber);
        }
    }

    @action.bound
    onSearchTermChanged(_?: React.ChangeEvent<HTMLInputElement>, newValue?: string) {
        this.searchString = newValue || '';
        this._persistFilter();
    }

    @computed
    get textComparer() {
        const comparator: MatchComparer = (v, t, s) => {
            return stringSearchService.prepareString(v)?.indexOf(stringSearchService.prepareString(t) ?? '', s) ?? -1;
        };
        return comparator;
    }

    @computed
    get searchTerms() {
        const searchStr = this.searchString;
        return searchStr.split(/[ ,]+/).filter((x) => !!x);
    }

    @computed
    public get visibleAircrafts() {
        return this.aircrafts
            .filter(a => this.includeInactive || AircraftHelper.isInServiceNow(a))
            .filter(a => !this.onlyWk || a.airlineRcd === AircraftsStore.ActiveCarrierCode);
    }

    @computed
    public get filteredAircrafts() {
        let filteredList = [...this.visibleAircrafts];
        const searchStringParts = this.searchTerms;

        if (searchStringParts.length) {
            const comparator = this.textComparer;

            filteredList = filteredList.filter((item: AircraftDto): boolean => {
                for (const term of searchStringParts) {
                    if (comparator(item.registrationNumber, term, 0) !== -1) {
                        return true;
                    }
                    if (comparator(item.aircraftTypeRcd, term, 0) !== -1) {
                        return true;
                    }
                }
                return false;
            });
        }

        return [...filteredList];
    }
}
